import type { PartnerClient } from '@epilot/sdk/identity'
import type { ProviderSetup } from '@epilot/sdk/phone-integration'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'

import { setupFormSchema, SetupFormValues, toSetupRequest } from './validation'

import { getPhoneIntegrationClient } from '@/api/phone-integration-client'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { toast } from '@/components/ui/use-toast'
import { EPILOT_IDP_PATH, ErrorNotice } from '@/modules/epilot-idp/shared'
import {
  useAllOidcClients,
  useOidcOrganizations
} from '@/services/identity.service'
import { useStoreProviderSetup } from '@/services/phone-integration.service'

/** Active applications first (the only ones Babelforce can sign in with), then by name. */
const byStatusThenName = (a: PartnerClient, b: PartnerClient) =>
  Number(b.status === 'active') - Number(a.status === 'active') ||
  a.display_name.localeCompare(b.display_name)

/**
 * Warns (without blocking) when the organization is not enabled on the chosen application: SSO would be configured
 * at Babelforce, but nobody could sign in and assigning agents would fail on the subject read.
 */
const EnablementNotice = ({
  clientId,
  orgId
}: {
  clientId: string
  orgId: string
}) => {
  const { data: organizations, isSuccess } = useOidcOrganizations(clientId)
  const enablement = organizations?.find((item) => item.org_id === orgId)

  if (!isSuccess || enablement?.status === 'active') return null

  return (
    <Alert>
      <AlertDescription>
        This organization is{' '}
        {enablement
          ? `${enablement.status} on this application`
          : 'not enabled on this application'}{' '}
        in Epilot IdP. Its agents cannot sign in to the phone, and assigning
        agents fails, until it is enabled.{' '}
        <Link
          className="underline"
          to={`${EPILOT_IDP_PATH}/${encodeURIComponent(clientId)}`}
        >
          Open the application
        </Link>
      </AlertDescription>
    </Alert>
  )
}

/**
 * Runs `setupProvider` for one organization. The client secret lives only in this form's state: it is
 * sent once, cleared after a successful run, and never put into React Query (no `useMutation`, whose
 * cache keeps the variables), storage, URLs, logs or toasts. `onSetUp` runs after a successful run.
 */
export const ProviderSetupForm = ({
  orgId,
  setup,
  suggestedAccountName,
  onSetUp
}: {
  orgId: string
  setup: ProviderSetup
  suggestedAccountName?: string
  onSetUp?: () => void
}) => {
  const needsAccountName = !setup.tenant
  const schema = useMemo(
    () => setupFormSchema(needsAccountName),
    [needsAccountName]
  )
  const applications = useAllOidcClients()
  const options = useMemo(
    () => [...(applications.data ?? [])].sort(byStatusThenName),
    [applications.data]
  )
  const hasActive = options.some((option) => option.status === 'active')
  const storeSetup = useStoreProviderSetup()
  const [error, setError] = useState('')
  const form = useForm<SetupFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      account_name: suggestedAccountName ?? '',
      identity_client_id: '',
      identity_client_secret: ''
    },
    mode: 'onChange'
  })
  const busy = form.formState.isSubmitting
  const clientId = form.watch('identity_client_id')

  // The operator chooses; only the application Babelforce's SSO already points at is preselected.
  useEffect(() => {
    if (form.getValues('identity_client_id')) return
    const current = options.find(
      (option) =>
        option.client_id === setup.sso.identity_client_id &&
        option.status === 'active'
    )

    if (current)
      form.setValue('identity_client_id', current.client_id, {
        shouldValidate: true
      })
  }, [options, form, setup.sso.identity_client_id])

  const onSubmit = async (values: SetupFormValues) => {
    setError('')
    try {
      // never cached or retried by a hook: the request body holds the client secret
      const { data: result } = await getPhoneIntegrationClient().setupProvider(
        { org_id: orgId },
        toSetupRequest(values, needsAccountName)
      )

      storeSetup(result)
      form.resetField('identity_client_secret', { defaultValue: '' })
      toast({
        title: result.ready ? 'Phone is set up' : 'Setup ran',
        description: result.ready
          ? 'Phone agents can now be assigned in this organization.'
          : 'Some steps are still missing. Check the status above.'
      })
      onSetUp?.()
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'The setup failed. Please try again.'

      setError(message)
      toast({
        title: 'Error',
        description: `Could not set up the phone. ${message}`,
        variant: 'destructive'
      })
    }
  }

  return (
    <Form {...form}>
      <form
        aria-label="Phone setup"
        className="space-y-4"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <fieldset className="space-y-4" disabled={busy}>
          {needsAccountName && (
            <FormField
              control={form.control}
              name="account_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Babelforce account name</FormLabel>
                  <FormControl>
                    <Input maxLength={100} {...field} />
                  </FormControl>
                  <FormDescription>
                    Name of the organization’s sub-account at Babelforce. Must
                    be unique across Babelforce; set once, when the sub-account
                    is created.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <FormField
            control={form.control}
            name="identity_client_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Epilot IdP application</FormLabel>
                <Select
                  disabled={busy || !hasActive}
                  name={field.name}
                  onValueChange={field.onChange}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger id="identity_client_id">
                      <SelectValue
                        placeholder={
                          applications.isPending
                            ? 'Loading applications…'
                            : 'Select an application'
                        }
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {options.map((application) => (
                      <SelectItem
                        disabled={application.status !== 'active'}
                        key={application.client_id}
                        value={application.client_id}
                      >
                        {application.display_name} · {application.client_id}
                        {application.status !== 'active' &&
                          ` · ${application.status}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>
                  Every Epilot IdP application of this stage; choose the one
                  Babelforce signs this organization’s agents in with. Only
                  active applications can be chosen. Babelforce’s SSO
                  configuration for the organization is replaced on every run.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <ErrorNotice
            message={
              applications.error
                ? `Could not load the Epilot IdP applications. ${applications.error.message}`
                : undefined
            }
          />
          {applications.isSuccess && !hasActive && (
            <Alert>
              <AlertDescription>
                {options.length
                  ? 'None of the Epilot IdP applications is active.'
                  : 'There is no application in Epilot IdP yet.'}{' '}
                <Link className="underline" to={EPILOT_IDP_PATH}>
                  {options.length ? 'Activate one' : 'Create and activate one'}
                </Link>{' '}
                first.
              </AlertDescription>
            </Alert>
          )}
          {clientId && <EnablementNotice clientId={clientId} orgId={orgId} />}
          <FormField
            control={form.control}
            name="identity_client_secret"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Application client secret</FormLabel>
                <FormControl>
                  <Input
                    autoComplete="new-password"
                    maxLength={512}
                    spellCheck={false}
                    type="password"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  The application’s current secret. It is sent to Babelforce’s
                  SSO configuration and is not stored by epilot. After a secret
                  rotation, run the setup again with the new secret.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <ErrorNotice message={error} />
          <Button loading={busy} type="submit">
            {setup.tenant ? 'Run setup again' : 'Set up'}
          </Button>
        </fieldset>
      </form>
    </Form>
  )
}
